import { DataPoint } from "@soft-db/d3-chart";
import {
	accelerationUnits,
	concentrationUnits,
	DataType,
	flowRateUnits,
	humidityUnits,
	pressureUnits,
	rainRateUnits,
	soundUnits,
	temperatureUnits,
	Time,
	Unit,
	UnitClasses,
	velocityUnits,
	VibrationSubType,
	WeatherDataType,
	windDirectionUnits,
	windSpeedUnit,
} from "@soft-db/data-formatter";

export const useUnits = () => {
	const possibilities = (units: UnitClasses): readonly string[] => {
		switch (units) {
			case "acceleration":
				return accelerationUnits;
			case "humidity":
				return humidityUnits;
			case "pressure":
				return pressureUnits;
			case "rain_rate":
				return rainRateUnits;
			case "sound":
			case "overpressure":
				return soundUnits;
			case "temperature":
				return temperatureUnits;
			case "velocity":
				return velocityUnits;
			case "wind_direction":
				return windDirectionUnits;
			case "wind_speed":
				return windSpeedUnit;
			case "concentration":
				return concentrationUnits;
			case "flow_rate":
				return flowRateUnits;
			default:
				return [] as string[];
		}
	};

	const convertY = (
		dataPoints: DataPoint[],
		subType: VibrationSubType | WeatherDataType | "",
		unit: Unit
	) => {
		const multiplicand = findMultiplicand(subType, unit);

		if (multiplicand === 1) return dataPoints;

		let addent = 0;
		if (unit === "F") addent = 32;

		const convertedData: DataPoint[] = [];
		for (const point of dataPoints) {
			if (point.y == null) {
				convertedData.push(point);
			} else {
				convertedData.push({ x: point.x, y: point.y * multiplicand + addent });
			}
		}

		return convertedData;
	};

	const convertTo = (value: number, unit: Unit) => {
		return value * vibrationMultiplicand(unit);
	};

	const convertFrom = (value: number, unit: Unit) => {
		return value / vibrationMultiplicand(unit);
	};

	const convertTime = (value: number, to: Time) => {
		if (to === "ms") return value * 1000;
		else return value / 1000;
	};

	const findMultiplicand = (subType: VibrationSubType | WeatherDataType | "", unit: Unit) => {
		switch (subType) {
			case "acceleration":
			case "velocity":
				return vibrationMultiplicand(unit);
			case "pressure":
				return pressureMultiplicand(unit);
			case "rain_rate":
				return rainRateMultiplicand(unit);
			case "temperature":
				return temperatureMultiplicand(unit);
			case "wind_speed":
				return windSpeedMultiplicand(unit);
			case "humidity":
			case "wind_direction":
			case "":
			default:
				return 1;
		}
	};

	const vibrationMultiplicand = (unit: Unit) => {
		switch (unit) {
			case "μm/s":
			case "μm/s²":
				return 1_000_000;
			case "mm/s":
			case "mm/s²":
				return 1_000;
			case "cm/s":
			case "cm/s²":
				return 100;
			case "dm/s":
			case "dm/s²":
				return 10;
			case "μinch/s":
			case "μinch/s²":
				return 39370078.74;
			case "inch/s":
			case "inch/s²":
				return 39.37007874;
			case "g":
				return 0.10197162129779283;
			case "m/s":
			default:
				return 1;
		}
	};
	const pressureMultiplicand = (unit: Unit) => {
		switch (unit) {
			case "kPa":
				return 0.001;
			case "psi":
				return 0.00014503263234228;
			case "mmHg":
				return 0.00751879699248;
			case '"Hg':
				return 0.000295333727;
			case "atm":
				return 0.00000986923266;
			case "Pa":
			default:
				return 1;
		}
	};
	const rainRateMultiplicand = (unit: Unit) => {
		switch (unit) {
			case "cm/h":
				return 0.1;
			case "m/h":
				return 0.001;
			case "inch/h":
				return 0.03937007874;
			case "mm/h":
			default:
				return 1;
		}
	};
	const temperatureMultiplicand = (unit: Unit) => {
		switch (unit) {
			case "F":
				return 1.8;
			case "C":
			default:
				return 1;
		}
	};
	const windSpeedMultiplicand = (unit: Unit) => {
		switch (unit) {
			case "m/h":
				return 3_600;
			case "km/h":
				return 3.6;
			case "inch/h":
				return 141732.28346457;
			case "mph":
				return 2.236936;
			case "m/s":
			default:
				return 1;
		}
	};

	const specifierFormat = (
		type: DataType | "",
		subType?: VibrationSubType | WeatherDataType | ""
	) => {
		if (type === "vibration" || type === "dust") return ".4~g";
		if (subType === "pressure" || subType === "rain_rate") return ".3~f";

		return ".1~f";
	};

	return { possibilities, convertY, convertTo, convertFrom, convertTime, specifierFormat };
};
